import { EditOptionsType } from '@redux/crudCreator/types';
import { Table, TableProps } from 'antd';
import { SortOrder } from 'antd/es/table/interface';
import IActionCol from 'components/rest/Table/ActionCol';
import { DraggableBodyRow } from 'components/rest/Table/DraggableBodyRow';
import TableColumn, {
  IRestColumnProps,
} from 'components/rest/Table/TableColumn';
import _, { get, omit } from 'lodash';
import React, {
  // FC,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DynamicObjectType } from 'types/commons';
import updateArray from 'immutability-helper';
import { useTranslation } from 'react-i18next';
import { separateThousand } from 'utils/formattedString';
import SelectionRow from 'components/rest/Table/SelectionRow';
import { useDispatch } from '@redux';
import crudActions from '@redux/crudActions';

export interface IRestTableProps
  extends TableProps<{ id: string } & DynamicObjectType> {
  model?: string;
  columns: IRestColumnProps[];
  orderBy?: string;
  hasDraggable?: boolean;
  isGoShow?: boolean;
  gotoShowPage?: (id: string) => void;
  gotoEditPage?: (id: string) => void;
  total?: number;
  onChangePagination: (
    {
      current,
      pageSize,
      total,
    }: {
      current: number;
      pageSize: number;
      total: number;
    },
    filters: any,
    sorter: any,
  ) => void;
  hasDel?: boolean;
  hasShow?: boolean;
  hasEdit?: boolean;
  extraActions?: React.ReactElement[];
  page?: number;
  pageSize?: number;
  showColumns?: string[];
  handleEditRow?: (data: DynamicObjectType, options: EditOptionsType) => void;
  updatePosition?: ({
    dragRow,
    hoverRow,
    firstItem,
    newList,
  }: {
    dragRow?: { id: string };
    hoverRow?: { id: string };
    firstItem?: { id: string };
    newList?: { id: string }[];
  }) => void;
  rowSelectionActions?: React.ReactNode;
  initSelectedRowKeys?: React.Key[];
  handleSelectedRowKeys?: (
    selectedRowKeys: React.Key[],
    selectedRows: DynamicObjectType[],
  ) => void;
  rowKey?: string;
  customDescDelete?: string;
  rowClassName?: (record: DynamicObjectType, index: number) => string;
}

const RestTable = (
  {
    orderBy,
    dataSource,
    columns = [],
    hasDraggable,
    isGoShow = false,
    gotoShowPage,
    gotoEditPage,
    loading = false,
    model,
    total,
    onChangePagination,
    hasShow,
    hasEdit,
    hasDel,
    extraActions,
    pageSize,
    page,
    showColumns,
    handleEditRow,
    updatePosition,
    rowSelectionActions,
    initSelectedRowKeys,
    handleSelectedRowKeys,
    expandable,
    rowKey = 'id',
    tableLayout,
    scroll,
    customDescDelete,
    rowClassName,
    title,
    className,
  }: IRestTableProps,
  ref,
) => {
  const dispatch = useDispatch();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
    () => initSelectedRowKeys || [],
  );
  const { t } = useTranslation();

  function extractIds(data) {
    const ids = [];

    function extractIdsRecursively(element) {
      if (element && !ids.includes(element?.[rowKey])) {
        ids.push(element?.[rowKey]);
        if (_.has(element, 'children') && element?.children?.length > 0) {
          _.forEach(element.children, extractIdsRecursively);
        }
      }
    }

    _.forEach(data, extractIdsRecursively);
    return ids;
  }

  const handleSelect = (value, selected, newSelectedRows) => {
    const cloneIDs = [...selectedRowKeys];
    if (_.includes(cloneIDs, value?.[rowKey])) {
      let idsChildren = [];
      if (_.has(value, 'children') && value?.children?.length > 0) {
        idsChildren = extractIds(value?.children);
      }
      const removeIDs = _.pull(
        cloneIDs,
        ...idsChildren,
        value?.[rowKey]?.split('-')?.[0],
        value?.[rowKey],
      );
      setSelectedRowKeys(removeIDs);
      dispatch(crudActions[model].setSelectedRowKeys(removeIDs));
      handleSelectedRowKeys?.(removeIDs, newSelectedRows);
    } else {
      let idsChildren = [];
      if (_.has(value, 'children') && value?.children?.length > 0) {
        idsChildren = extractIds(value?.children);
      }
      const addIDs = _.uniq([...cloneIDs, ...idsChildren, value?.[rowKey]]);
      setSelectedRowKeys(addIDs);
      dispatch(crudActions[model].setSelectedRowKeys(addIDs));
      handleSelectedRowKeys?.(addIDs, newSelectedRows);
    }
  };

  const handleSelectAll = (selected, newSelectedRows, changeRows) => {
    const convertArray = changeRows.map((item) => item?.[rowKey]);
    if (selected) {
      setSelectedRowKeys((value) => {
        const newSelected = _.uniq([...value, ...convertArray]);

        // dataSource.forEach((e) => {
        //   if (
        //     newSelected.includes(e[rowKey]) &&
        //     e.children?.length &&
        //     !e.children.find((child) => newSelected.includes(child[rowKey]))
        //   ) {
        //     newSelected = newSelected.filter((el) => el !== e[rowKey]);
        //   }
        // });
        // newSelected.forEach((e) => {
        //   if (e?.split('-').length > 1) {
        //     newSelected.push(e.split('-')[0]);
        //   }
        // });
        handleSelectedRowKeys?.(newSelected, newSelectedRows);
        dispatch(crudActions[model].setSelectedRowKeys(newSelected));
        return newSelected;
      });
    } else {
      const cloneIDs = [...selectedRowKeys];
      const removeElementDup = _.pull(cloneIDs, ...convertArray);

      // const newSelectedRowKeys = [...selectedRowKeys];
      // let removeElementDup = _.difference(
      //   _.union(newSelectedRowKeys, convertArray),
      //   _.intersection(newSelectedRowKeys, convertArray),
      // );

      // dataSource.forEach((e) => {
      //   if (
      //     removeElementDup.includes(e[rowKey]) &&
      //     e.children?.length &&
      //     !e.children.find((child) => removeElementDup.includes(child[rowKey]))
      //   ) {
      //     removeElementDup = removeElementDup.filter((el) => el !== e[rowKey]);
      //   }
      // });
      // removeElementDup.forEach((e) => {
      //   if (`${e}`?.split('-').length > 1) {
      //     removeElementDup.push(`${e}`?.split('-')[0]);
      //   }
      // });
      setSelectedRowKeys(removeElementDup);
      dispatch(crudActions[model].setSelectedRowKeys(removeElementDup));
      handleSelectedRowKeys?.(removeElementDup, newSelectedRows);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      handleSelectRows: (selected) => {
        setSelectedRowKeys(selected);
      },
    }),
    [],
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onSelect: handleSelect,
      onSelectAll: handleSelectAll,
    }),
    [selectedRowKeys, handleSelect, handleSelectAll],
  );
  const hasSelected = useMemo(
    () => selectedRowKeys.length > 0,
    [selectedRowKeys],
  );

  const components = useMemo(() => {
    return hasDraggable
      ? {
          body: {
            row: DraggableBodyRow,
          },
        }
      : undefined;
  }, [hasDraggable]);

  const _columns = useMemo(() => {
    const tmp = columns
      .filter((e) => showColumns.includes(e.name))
      .map((e) => ({
        ...omit(e, ['sorter', 'name', 'isDefault']),
        dataIndex: `${e.name}`,
        ...(e.sorter
          ? {
              sorter: e.sorter
                ? (a, b) => get(a, e.name) - get(b, e.name)
                : undefined,
            }
          : {}),
        ...(e.sorter && getSorterOrder(orderBy, e.name)
          ? {
              defaultSortOrder: getSorterOrder(orderBy, e.name),
            }
          : {}),
        title: typeof e.title === 'string' ? t(e.title.toString()) : e.title,
        width: e?.width,
        ellipsis: e?.ellipsis,
        render: (obj, record, index) => (
          <TableColumn
            handleEditRow={handleEditRow}
            render={e.render}
            name={e.name}
            model={model}
            data={obj}
            record={record}
            index={index}
          />
        ),
        children: e?.children?.map((child) => ({
          ...omit(child, ['sorter', 'name', 'isDefault']),
          dataIndex: `${child.name}`,
          ...(child.sorter
            ? {
                sorter: child.sorter
                  ? (a, b) => get(a, child.name) - get(b, child.name)
                  : undefined,
              }
            : {}),
          ...(child.sorter && getSorterOrder(orderBy, child.name)
            ? {
                defaultSortOrder: getSorterOrder(orderBy, child.name),
              }
            : {}),
          title:
            typeof e.title === 'string'
              ? t(child.title.toString())
              : child.title,
          render: (obj, record, index) => (
            <TableColumn
              handleEditRow={handleEditRow}
              render={child.render}
              name={child.name}
              model={model}
              data={obj}
              record={record}
              index={index}
            />
          ),
        })),
      }));
    if (hasEdit || hasDel || hasShow || extraActions) {
      tmp.push({
        title: 'Action',
        dataIndex: `action`,
        align: 'center',
        width: 200,
        ellipsis: false,
        render: (d, r, i) => (
          <IActionCol
            gotoShowPage={gotoShowPage}
            gotoEditPage={gotoEditPage}
            hasShow={hasShow}
            hasDel={hasDel}
            hasEdit={hasEdit}
            extraActions={extraActions}
            data={d}
            record={r}
            index={i}
            setSelectedRowKeys={setSelectedRowKeys}
            selectedRowKeys={selectedRowKeys}
            model={model}
            customDescDelete={customDescDelete}
          />
        ),
        children: [],
      });
    }
    return tmp;
  }, [
    columns,
    hasEdit,
    hasDel,
    hasShow,
    showColumns,
    selectedRowKeys,
    setSelectedRowKeys,
  ]);

  const pagination = useMemo(() => {
    return {
      current: page,
      pageSize,
      total,
      showTotal: (_total, range) =>
        t('common.total', {
          start: separateThousand(range[0]),
          end: separateThousand(range[1]),
          total: separateThousand(total),
        }),
    };
  }, [total, page, pageSize]);

  const moveRow = useCallback((dragIndex, hoverIndex) => {
    //
    if (!hasDraggable) return;
    const dragRow = dataSource[dragIndex];
    const hoverRow = dataSource[hoverIndex];

    updatePosition?.({
      dragRow,
      hoverRow,
      firstItem: dataSource[0],
      newList: updateArray([...dataSource], {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRow],
        ],
      }),
    });
  }, []);

  const onRow = useCallback(
    (record, index) => ({
      index,
      moveRow,
      onClick: () => isGoShow && gotoShowPage(record.id),
    }),
    [moveRow],
  );

  const tableExpandable = useMemo(
    () =>
      expandable
        ? {
            ...expandable,
            expandedRowKeys: expandable?.defaultExpandAllRows
              ? dataSource.map((e) => e.id)
              : expandable?.expandedRowKeys,
          }
        : undefined,
    [expandable, dataSource],
  );

  useEffect(() => {
    initSelectedRowKeys && setSelectedRowKeys(initSelectedRowKeys || []);
    initSelectedRowKeys &&
      dispatch(
        crudActions[model].setSelectedRowKeys(initSelectedRowKeys || []),
      );
  }, [initSelectedRowKeys]);

  useEffect(() => {
    if (handleSelectedRowKeys) {
      handleSelectedRowKeys(selectedRowKeys, []);
    }
  }, [selectedRowKeys]);

  return (
    <>
      {!!rowSelectionActions && hasSelected && (
        <SelectionRow
          selected={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
        >
          {rowSelectionActions}
        </SelectionRow>
      )}
      <DndProvider backend={HTML5Backend}>
        <Table<{ id: string; key?: string }>
          onRow={onRow}
          components={components}
          rowKey={rowKey}
          pagination={pagination}
          onChange={onChangePagination}
          dataSource={dataSource}
          columns={_columns}
          loading={loading}
          rowSelection={rowSelectionActions ? rowSelection : undefined}
          expandable={tableExpandable}
          tableLayout={tableLayout}
          scroll={scroll}
          rowClassName={rowClassName}
          title={title}
          className={className}
          sortDirections={['descend', 'ascend']}
        />
      </DndProvider>
    </>
  );
};

const getSorterOrder = (
  orderBy: string,
  name: string,
): SortOrder | undefined => {
  if (orderBy === name) return 'ascend';
  if (orderBy === `-${name}`) return 'descend';
  return undefined;
};

export default forwardRef(RestTable);
